/**
 * Interested-buyers section for the listing detail page (B2).
 *
 * Presentational surface for the leads linked to a listing. All write
 * state (optimistic add/remove + rollback) lives in the parent page; this
 * component only renders the current view and calls back on intent.
 *
 * Soft-degrade: when the leads fetch failed, this section shows an inline
 * retry — the rest of the page (publish/delete/mark-sold) keeps working.
 */

import { Phone, Plus, Search, UserPlus, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import type { LeadWithLinks } from "@/features/realtor/services/leads-api.service";
import { TEMPERATURE_DOT_CLASS } from "@/features/realtor/utils/lead-format";
import { formatBudgetRange } from "@/features/realtor/utils/listings-format";
import { StudioButton } from "@/features/studio/theme";

interface InterestedBuyersSectionProps {
	interested: LeadWithLinks[];
	eligible: LeadWithLinks[];
	leadsError: boolean;
	busy: boolean;
	onRetry: () => void;
	onAdd: (leadId: string) => void;
	onUnlink: (leadId: string) => void;
}

export function InterestedBuyersSection({
	interested,
	eligible,
	leadsError,
	busy,
	onRetry,
	onAdd,
	onUnlink,
}: InterestedBuyersSectionProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	return (
		<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
			<CardContent className="p-5 space-y-3">
				<div className="flex items-center justify-between gap-2">
					<div>
						<p
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 11,
								fontWeight: 600,
								letterSpacing: "0.18em",
								textTransform: "uppercase",
								color: "var(--color-dashboard-muted)",
							}}
						>
							{t("realtor.listings.detail.interestedBuyersTitle")}
						</p>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontSize: 20,
								fontWeight: 500,
								fontStyle: "italic",
								color: "var(--color-dashboard-text)",
								marginTop: 4,
								lineHeight: 1.1,
							}}
						>
							Interested buyers
						</h2>
					</div>
					<AddBuyerPicker
						eligible={eligible}
						linkedCount={interested.length}
						disabled={busy || leadsError}
						onAdd={onAdd}
					/>
				</div>

				{leadsError ? (
					<div
						className="rounded-lg border border-dashed border-dashboard-border p-4 text-center space-y-2"
						role="alert"
					>
						<p className="text-xs text-dashboard-muted">
							{t("realtor.listings.detail.interestedBuyersLoadFailed")}
						</p>
						<StudioButton size="sm" variant="secondary" onClick={onRetry}>
							{t("realtor.listings.detail.interestedBuyersRetry")}
						</StudioButton>
					</div>
				) : interested.length === 0 ? (
					<p className="text-xs text-dashboard-muted">
						{t("realtor.listings.detail.interestedBuyersEmpty")}
					</p>
				) : (
					<ul className="space-y-2">
						{interested.map((entry) => (
							<BuyerRow
								key={entry.lead.id}
								entry={entry}
								busy={busy}
								onOpen={() => navigate(`/realtor/leads/${entry.lead.id}`)}
								onUnlink={() => onUnlink(entry.lead.id)}
							/>
						))}
					</ul>
				)}
			</CardContent>
		</Card>
	);
}

function BuyerRow({
	entry,
	busy,
	onOpen,
	onUnlink,
}: {
	entry: LeadWithLinks;
	busy: boolean;
	onOpen: () => void;
	onUnlink: () => void;
}) {
	const { t } = useTranslation();
	const { lead } = entry;
	const budget = formatBudgetRange(lead.budgetMin, lead.budgetMax);

	return (
		<li className="flex items-center gap-2 rounded-lg border border-dashboard-border bg-dashboard-bg p-2.5 hover:bg-dashboard-surface transition-colors">
			<button
				type="button"
				onClick={onOpen}
				className="flex-1 min-w-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-dashboard-accent rounded-md"
			>
				<span className="flex items-center gap-2">
					<span className="text-sm font-medium text-dashboard-text truncate">
						{lead.name}
					</span>
					<TempDot temperature={lead.temperature} />
				</span>
				<span className="block text-xs text-dashboard-muted truncate">
					{t(`realtor.leads.temperatures.${lead.temperature}`)}
					{budget ? ` · ${budget}` : ""}
				</span>
			</button>
			{/* ph-no-capture: the number is in the href, which session
			    replay records verbatim. See lib/posthog.ts. */}
			<a
				href={`tel:${lead.phoneCountryCode}${lead.phone}`}
				className="ph-no-capture inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium text-dashboard-accent-dark bg-dashboard-accent/10 hover:bg-dashboard-accent/20 transition-colors shrink-0"
				aria-label={t("realtor.leads.callAriaLabel", { name: lead.name })}
			>
				<Phone className="h-3 w-3" aria-hidden="true" />
				{t("realtor.leads.callShort")}
			</a>
			{/*
			 * Unlink is intentionally confirm-free: it only removes the
			 * lead↔listing relationship (the lead itself is untouched) and is
			 * trivially reversible by re-linking from the Add picker. This is a
			 * deliberate deviation from the page's delete / mark-sold confirm
			 * dialogs, which guard irreversible actions.
			 */}
			<button
				type="button"
				onClick={onUnlink}
				disabled={busy}
				className="inline-flex items-center justify-center h-7 w-7 rounded-md text-dashboard-muted hover:text-dashboard-pill-risk hover:bg-dashboard-surface transition-colors disabled:opacity-40 shrink-0"
				aria-label={t("realtor.listings.detail.unlinkBuyerAriaLabel", {
					name: lead.name,
				})}
			>
				<X className="h-4 w-4" />
			</button>
		</li>
	);
}

interface AddBuyerPickerProps {
	eligible: LeadWithLinks[];
	/** Number of leads already linked to this listing (the "interested" list). */
	linkedCount: number;
	disabled: boolean;
	onAdd: (leadId: string) => void;
}

function AddBuyerPicker({
	eligible,
	linkedCount,
	disabled,
	onAdd,
}: AddBuyerPickerProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");

	const noEligible = eligible.length === 0;
	const triggerDisabled = disabled || noEligible;

	const filtered = useMemo(() => {
		const term = query.trim().toLowerCase();
		if (!term) return eligible;
		return eligible.filter((e) =>
			[e.lead.name, e.lead.phone, e.lead.email ?? ""]
				.join(" ")
				.toLowerCase()
				.includes(term),
		);
	}, [eligible, query]);

	const trigger = (
		<StudioButton
			type="button"
			size="sm"
			variant="secondary"
			className="gap-1.5 border-dashboard-border text-dashboard-text"
			disabled={triggerDisabled}
		>
			<Plus className="h-4 w-4" />
			{t("realtor.listings.detail.addBuyer")}
		</StudioButton>
	);

	// Empty-options guard: never open an empty dropdown. When there are no
	// eligible leads, the trigger is disabled and a tooltip explains why
	// ("No leads to add" vs "All leads already linked").
	if (triggerDisabled) {
		// Resolve the disabled reason in priority order:
		//   1. busy / leads fetch failed → neutral "temporarily unavailable";
		//      the disabled state is transient and unrelated to link state.
		//   2. no eligible options but some leads ARE already linked → all
		//      existing leads are on this listing ("All leads already linked").
		//   3. no eligible options and none linked → there are genuinely no
		//      leads yet ("No leads to add").
		let reason: string;
		if (disabled) {
			reason = "realtor.listings.detail.addBuyerDisabledUnavailable";
		} else if (linkedCount > 0) {
			reason = "realtor.listings.detail.addBuyerDisabledAllLinked";
		} else {
			reason = "realtor.listings.detail.addBuyerDisabledNoLeads";
		}
		return (
			<TooltipProvider>
				<Tooltip>
					{/* span wrapper so the tooltip still fires over a disabled button
					    (a disabled button swallows its own pointer events) */}
					<TooltipTrigger asChild>
						<span className="inline-flex">{trigger}</span>
					</TooltipTrigger>
					<TooltipContent>{t(reason)}</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		);
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>{trigger}</PopoverTrigger>
			<PopoverContent
				className="w-72 p-0 bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]"
				align="end"
			>
				<div className="flex items-center gap-2 border-b border-dashboard-border px-3 py-2">
					<Search className="h-4 w-4 shrink-0 text-dashboard-muted" />
					<Input
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder={t("realtor.listings.detail.addBuyerSearchPlaceholder")}
						aria-label={t("realtor.listings.detail.addBuyerSearchPlaceholder")}
						className="h-10 border-0 px-0 shadow-none focus-visible:ring-0 bg-transparent text-dashboard-text placeholder:text-dashboard-muted"
					/>
				</div>
				{/* The "no results" message must live OUTSIDE the listbox so the
				    listbox only ever contains option children. */}
				{filtered.length === 0 ? (
					<p className="px-3 py-4 text-center text-xs text-dashboard-muted">
						{t("realtor.listings.detail.addBuyerNoResults")}
					</p>
				) : (
					<div className="max-h-56 overflow-y-auto p-1" role="listbox">
						{filtered.map((entry) => (
							<button
								key={entry.lead.id}
								type="button"
								role="option"
								aria-selected={false}
								onClick={() => {
									onAdd(entry.lead.id);
									setOpen(false);
									setQuery("");
								}}
								className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-dashboard-text hover:bg-dashboard-surface transition-colors"
							>
								<UserPlus
									className="h-4 w-4 shrink-0 text-dashboard-muted"
									aria-hidden="true"
								/>
								<span className="min-w-0">
									<span className="block truncate">{entry.lead.name}</span>
									<span className="block truncate text-xs text-dashboard-muted">
										{t(`realtor.leads.temperatures.${entry.lead.temperature}`)}
									</span>
								</span>
							</button>
						))}
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}

function TempDot({ temperature }: { temperature: "hot" | "warm" | "cold" }) {
	const tone = TEMPERATURE_DOT_CLASS[temperature];
	return (
		<span
			className={`h-1.5 w-1.5 rounded-full shrink-0 ${tone}`}
			aria-hidden="true"
		/>
	);
}
